import { useMemo } from "react";
import { format } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { useInvestmentHistoricQuotes } from "@features/investments/investment/hooks/useInvestmentHistoricQuotes";
import { usePublicOrPrivatePerformance } from "@api/v1/hooks/performance/usePublicOrPrivatePerformance.ts";
import { getInterval } from "@helpers";
import {
  convertComparisonToChartData,
  convertToChartData,
} from "@hooks/chart/utils";
import { ComparisonValue } from "@components";

export interface InvestmentsChartDataStatistic {
  portfolioValue: number;
  performance: number;
  changeToday: number;
  investedCapital: number;
  changeTodayAbs: number;
  valuationGain: number;
  valuationGainPercent: number;
  cashChangeAbs: number;
  irr: number;
  holdings: number;
  cashBalance: number;
  realizedGain: number;
  dividendGain: number;
  dividendYield: number;
  areAccountLoading?: boolean;
}

export const useInvestmentsChartDataTotal = ({
  accountIds = [],
  startDate,
  endDate,
  chartType = "valuation",
  comparedTicker,
  areAccountsLoading,
}: {
  accountIds?: number[];
  startDate?: Date;
  endDate?: Date;
  chartType?: "performance" | "valuation" | "dividends";
  comparedTicker?: ComparisonValue;
  areAccountsLoading?: boolean;
}) => {
  const interval = getInterval(startDate, endDate);

  const {
    data: performanceData,
    isPending,
    isFetching,
    isFetched,
  } = usePublicOrPrivatePerformance({
    accountIds,
    startDate: format(startDate!, "yyyy-MM-dd"),
    endDate: format(endDate!, "yyyy-MM-dd"),
    interval,
    cashFlowAndPerformanceStatisticsParamsList: [
      {
        includeCash: false,
        includeDividends: true,
      },
      {
        includeCash: false,
        includeDividends: false,
      },
      {
        includeCash: true,
        includeDividends: true,
      },
    ],
  });

  const {
    data: comparisonHistoryQuotes = [],
    isPending: historicQuotesPending,
    isFetching: historicQuotesLoading,
  } = useInvestmentHistoricQuotes(
    {
      tickerSymbol: comparedTicker?.value,
      interval,
      from: startDate?.toISOString(),
      to: endDate?.toISOString(),
    },
    {
      staleTime: Infinity,
      enabled: Boolean(comparedTicker),
      placeholderData: keepPreviousData,
    }
  );

  const statistics = performanceData[0];
  const cashStatistics = performanceData[2];
  const aggregatedStatistics = performanceData[0]?.aggregatedGainLossStatistics;

  const twrorResult =
    statistics?.twrorResults[statistics.twrorResults.length - 1];
  const cashTwrorResult =
    cashStatistics?.twrorResults[statistics.twrorResults.length - 1];

  const portfolioValue = cashTwrorResult?.currentMarketValue ?? 0;
  const performance = (twrorResult?.twror ?? 1) - 1;
  const cashBalance = twrorResult?.cashBalance ?? 0;
  const cashChangeAbs =
    (cashTwrorResult?.cashBalance ?? 0) -
    (cashStatistics?.twrorResults[0]?.cashBalance ?? 0);
  const holdings = twrorResult?.currentMarketValue ?? 0;
  const changeToday = statistics?.changeLast?.performance ?? 0;
  const investedCapital = twrorResult?.purchaseValue ?? 0;
  const changeTodayAbs = statistics?.changeLast?.changeAbsCashFlowAdjusted ?? 0;
  const valuationGain = twrorResult?.valuationGainAbs ?? 0;
  const valuationGainPercent = twrorResult?.valuationGainInPercent ?? 0;
  const irr = statistics?.irrTotal?.irr ?? 0;
  const dividendGain = aggregatedStatistics?.grossDividendGain ?? 0;
  const dividendYield = aggregatedStatistics?.grossDividendYield ?? 0;
  const realizedGain = aggregatedStatistics?.grossTradingGain ?? 0;

  const comparedValuesWithDividends = useMemo(
    () =>
      comparedTicker
        ? convertComparisonToChartData(
            performanceData[0]?.twrorResults.map(({ date, twror }) => ({
              date,
              value: twror,
            })),
            comparisonHistoryQuotes,
            "performance"
          ).map((item) => item.mainValue)
        : [],
    [comparedTicker, comparisonHistoryQuotes, performanceData]
  );

  const comparedValues = useMemo(
    () =>
      comparedTicker
        ? convertComparisonToChartData(
            performanceData[1]?.twrorResults.map(({ date, twror }) => ({
              date,
              value: twror,
            })),
            comparisonHistoryQuotes,
            "performance"
          )
        : [],
    [comparedTicker, comparisonHistoryQuotes, performanceData]
  );

  const totalValuesWithDividends = convertToChartData(
    chartType === "performance" ? "twror" : "currentMarketValue",
    performanceData[0]?.twrorResults,
    chartType
  );
  const totalValues = convertToChartData(
    chartType === "performance" ? "twror" : "currentMarketValue",
    performanceData[1]?.twrorResults,
    chartType
  );
  const investedCapitalValues = convertToChartData(
    "purchaseValue",
    performanceData[0]?.twrorResults,
    chartType
  );

  return {
    data: {
      totalValuesWithDividends: comparedTicker
        ? comparedValuesWithDividends
        : totalValuesWithDividends,
      totalValues: comparedTicker
        ? comparedValues.map((item) => item.mainValue)
        : totalValues,
      comparisonValues: comparedValues?.map((item) => item.comparedValue),
      investedCapitalValues,
    },
    statistic: {
      portfolioValue,
      cashBalance,
      cashChangeAbs,
      holdings,
      performance,
      changeToday,
      investedCapital,
      changeTodayAbs,
      valuationGain,
      valuationGainPercent,
      irr,
      dividendGain,
      dividendYield,
      realizedGain,
    },
    isPending: isPending && historicQuotesPending,
    isFetching: isFetching || historicQuotesLoading || areAccountsLoading,
    isFetched: !accountIds?.length ? true : isFetched,
  };
};
